// useFilteredUids — Phase 7 Task 7-10b: client-local "uid 単位フィルタ" の
// state を保持する hook.
//
// 仕様 (architecture.md §19.14, protocol-events.md §5.1):
//   - 「特定ユーザの絵 / 発言を自分の画面で非表示にする」フィルタ.
//   - クライアントローカルで完結 (protocol には乗らない).
//   - drawing 側は future-only (engine.setStrokeFilter 経由), chat 側は
//     render 段で filter (= フィルタ解除で過去ログ復活).
//   - 自分の uid は **絶対に** filter に入れない (= imagepaste preview の
//     echo-as-ack が gate されると preview commit が hang する).
//
// このファイルが所有するのは `Set<string>` の保持と toggle/clear API のみ.
// engine.setStrokeFilter への伝搬は SessionProvider 側で `useEffect` を通じて
// 行う (filteredUids の参照変化を Provider が観測する形).
//
// vitest が node 環境で React component / hook を直接テストできない
// (vitest.config.ts:9) ので、純粋な状態遷移ロジックは `toggleUidIn` /
// `clearUids` という free function に切り出して個別 export する.
// hook 本体は `useState` でこの 2 関数をラップするだけの薄い shim.

import { useCallback, useMemo, useState } from 'react';

/**
 * Pure state transition for `toggleFilter`. Returns a NEW `Set<string>` on
 * change, or the SAME reference when the toggle is a no-op (= attempting to
 * toggle self-uid). Same-reference return on no-op lets React's `useState`
 * skip a re-render.
 *
 * @param current - 現在のフィルタ Set.
 * @param uid - 反転対象 uid.
 * @param selfUid - 自分の uid (= 一致したら no-op). `null` 許容 (welcome
 *   到着前は selfUid 未確定の可能性、その時点で toggle されることはほぼ無い
 *   が defensive).
 */
export const toggleUidIn = (
  current: ReadonlySet<string>,
  uid: string,
  selfUid: string | null,
): ReadonlySet<string> => {
  // Defensive: 自分の uid を toggle しようとしたら no-op.
  // UI (MemberPanel) でも先回りで自分の行のボタンを出さないが、二重防御として
  // ここでも block する (MemberPanel 以外の経路—例えば将来の slash command 等—
  // から呼ばれた時の事故を避ける).
  if (selfUid !== null && uid === selfUid) return current;
  if (current.has(uid)) {
    const next = new Set(current);
    next.delete(uid);
    return next;
  }
  const next = new Set(current);
  next.add(uid);
  return next;
};

/**
 * Pure clear. Returns the SAME reference when already empty (skip re-render).
 */
export const clearUids = (current: ReadonlySet<string>): ReadonlySet<string> => {
  if (current.size === 0) return current;
  return new Set<string>();
};

export interface UseFilteredUidsReturn {
  /** 現在のフィルタ Set (immutable view). */
  readonly filteredUids: ReadonlySet<string>;
  /**
   * Toggle filter on the given uid. Self-uid is a no-op (defensive).
   */
  readonly toggleFilter: (uid: string) => void;
  /** Clear all filters (将来 / debug 用). */
  readonly clear: () => void;
}

export interface UseFilteredUidsArgs {
  /**
   * 自分の uid. welcome 前は `null` 渡し可. self-uid との一致で toggleFilter
   * は no-op になる (`toggleUidIn` の挙動).
   */
  readonly selfUid: string | null;
}

/**
 * Phase 7 Task 7-10b: web ローカルの uid フィルタ state を保持する hook.
 * SessionProvider から呼ばれ、context 越しに ChatPanel / MemberPanel /
 * drawing engine effect に届く.
 */
export const useFilteredUids = ({ selfUid }: UseFilteredUidsArgs): UseFilteredUidsReturn => {
  const [filteredUids, setFilteredUids] = useState<ReadonlySet<string>>(
    () => new Set<string>() as ReadonlySet<string>,
  );

  const toggleFilter = useCallback(
    (uid: string): void => {
      setFilteredUids((prev) => toggleUidIn(prev, uid, selfUid));
    },
    [selfUid],
  );

  const clear = useCallback((): void => {
    setFilteredUids((prev) => clearUids(prev));
  }, []);

  return useMemo<UseFilteredUidsReturn>(
    () => ({ filteredUids, toggleFilter, clear }),
    [filteredUids, toggleFilter, clear],
  );
};
